import { logEvent } from '../lib/analytics'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { isOverlayBusy } from './Celebration'
import { railBusy, subscribeRail } from '../lib/toastRail'
import { capture } from '../lib/batchReceipt'
import { installMarkBurst, markBurstActive } from '../lib/markBurst'

// Never claimed — passed to railBusy() so "anyone at all holds the bottom rail"
// reads as busy. The medallion waits for the Undo bar (or a peek, a rating toast)
// to finish rather than covering it: a mark's Undo window has to come first, the
// celebration second (Jason 2026-09-20).
const RAIL_WAIT_ID = 'achievement'
// Enqueue a beat late so the Undo bar the same mark fired has claimed the rail
// before this checks it — both come out of the same saveProgress tick.
const ENQUEUE_DELAY_MS = 300

// ── AchievementUnlock ────────────────────────────────────────────────────────
// A real full-screen badge-mint moment for 'nextep:achievement-unlocked', rebuilt
// from the design-tool recreation (AchievementUnlock.dc.html): the medallion drops
// in, impact-shakes, a shine sweep crosses it, then the label/description land.
//
// This REPLACES the old behaviour of routing achievement-unlocked through the
// bottom Celebration toast (see Celebration.jsx's onAchievement, removed in the
// same change) — a toast under-sold the moment the design doc called for, and
// having BOTH a toast and a full-screen mint for the same event would have been
// a redundant double-surface (the same reasoning that already keeps card-minted
// off the toast rail, per Celebration.jsx's comment above its queue).
//
// Real data only: label/desc/icon come straight off the CustomEvent detail, which
// Achievements.jsx populates from ACHIEVEMENTS (src/lib/achievements.js) — the
// canonical achievement list. Tier (bronze/silver/gold, purely a colour choice)
// is derived from the achievement's real `target` since the data model has no
// explicit tier field: target ≥ 50 → gold, ≥ 10 → silver, else bronze. Multiple
// unlocks queue and play one at a time, same FIFO discipline as Celebration.
//
// Coordinates with the other full-screen moments the same way CardReveal does:
// holds behind isOverlayBusy() (a wrap clapboard / FinalCut / another card reveal
// already owns the screen), and raises __nextepFinaleActive itself while showing
// so nothing else jumps in on top of a medallion mid-drop.
const TIER_COLOR = { bronze: '#cd7f32', silver: '#c8c8d2', gold: '#f5c842' }
const tierFor = (target) => (target >= 50 ? 'gold' : target >= 10 ? 'silver' : 'bronze')

export function AchievementUnlockListener() {
  const navigate = useNavigate()
  const [queue, setQueue] = useState([])
  const [stage, setStage] = useState(0)
  const [, setGate] = useState(0)
  const timers = useRef([])
  const idRef = useRef(0)

  useEffect(() => {
    const pending = []
    const onAchievement = (e) => {
      const a = e.detail
      if (!a?.label) return
      if (capture('achievement', e)) return   // a cascade's receipt lists it instead
      pending.push(setTimeout(() => setQueue(q => [...q, { ...a, _id: ++idRef.current }]), ENQUEUE_DELAY_MS))
    }
    installMarkBurst()
    window.addEventListener('nextep:achievement-unlocked', onAchievement)
    return () => { window.removeEventListener('nextep:achievement-unlocked', onAchievement); pending.forEach(clearTimeout) }
  }, [])

  const item = queue[0] || null
  // Once we START presenting an item we raise __nextepFinaleActive ourselves
  // (below) so nothing else jumps in on top of us — but isOverlayBusy() reads
  // that SAME flag, so re-deriving `held` from it on every render would make
  // the component hide itself the instant it raises its own flag. presentingId
  // latches "started" per item so isOverlayBusy() is only consulted to decide
  // whether to START, never to decide whether to keep showing.
  const presentingId = useRef(null)
  // …and while someone is mid-run on Watched (markBurst): the medallion landing between
  // two marks gets rage-tapped away, not read. It waits for the marks to pause.
  const held = !!item && presentingId.current !== item._id && (isOverlayBusy() || railBusy(RAIL_WAIT_ID) || markBurstActive())

  // Re-check the overlay gate periodically while held, same pattern as Celebration,
  // and the moment the rail frees up.
  useEffect(() => {
    if (!held) return
    const iv = setInterval(() => setGate(g => g + 1), 450)
    return () => clearInterval(iv)
  }, [held])
  useEffect(() => subscribeRail(() => setGate(g => g + 1)), [])

  const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

  useEffect(() => {
    timers.current.forEach(clearTimeout)
    timers.current = []
    if (!item || held) { setStage(0); return }
    presentingId.current = item._id
    if (typeof window !== 'undefined') window.__nextepFinaleActive = true
    logEvent('cinematic_shown', { kind: 'achievement', id: item.id })
    if (reduce) { setStage(3); return }
    setStage(0)
    timers.current.push(
      setTimeout(() => setStage(1), 60),
      setTimeout(() => setStage(2), 260),
      setTimeout(() => setStage(3), 660),
    )
    return () => timers.current.forEach(clearTimeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?._id, held])

  const dismiss = () => {
    if (!item) return
    if (typeof window !== 'undefined') window.__nextepFinaleActive = false
    setQueue(q => q.slice(1))
  }

  // Auto-dismiss after the text has had time to read, unless held.
  useEffect(() => {
    if (!item || held || stage < 3) return
    const t = setTimeout(dismiss, 3400)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?._id, held, stage])

  if (!item || held) return null

  const tier = tierFor(item.target ?? 0)
  const color = TIER_COLOR[tier]
  const rgb = color === '#cd7f32' ? '205,127,50' : color === '#c8c8d2' ? '200,200,210' : '245,200,66'

  return createPortal((
    <div
      onClick={dismiss}
      style={{
        position: 'fixed', inset: 0, zIndex: 'var(--z-celebration)',
        background: `radial-gradient(circle at 50% 38%, rgba(${rgb},0.16) 0%, rgba(var(--bg-rgb),0.94) 65%), rgba(var(--bg-rgb),0.82)`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font)', cursor: 'pointer',
      }}
    className={reduce ? undefined : 'au-fade-in'}
    >
      <div style={{ position: 'relative', width: 180, height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
        {stage >= 1 && !reduce && (
          <>
            <div style={{ position: 'absolute', inset: 0, borderRadius: 'var(--radius-full)', border: `2px solid ${color}` }} className="au-ring" />
            <div style={{ position: 'absolute', inset: 0, borderRadius: 'var(--radius-full)', border: `2px solid ${color}` }} className="au-ring au-ring-2" />
          </>
        )}
        <div style={{
          position: 'absolute', inset: -10, borderRadius: 'var(--radius-full)',
          background: `radial-gradient(circle, rgba(${rgb},0.5), transparent 70%)`, filter: 'blur(14px)',
        }} className={reduce ? undefined : 'au-glow'} />
        <div style={{
          position: 'relative', width: 128, height: 128, borderRadius: 'var(--radius-full)',
          background: `linear-gradient(155deg, ${color}, rgba(0,0,0,0.35))`,
          border: `3px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 10px 40px rgba(0,0,0,0.6), 0 0 40px rgba(${rgb},0.5)`, overflow: 'hidden',
        }} className={reduce ? undefined : (stage >= 1 ? 'au-shake' : 'au-drop')}>
          <div style={{ position: 'absolute', inset: 6, borderRadius: 'var(--radius-full)', border: '1.5px dashed #fff', opacity: 0.5 }} />
          <span style={{ fontSize: 52, position: 'relative', zIndex: 2 }}>{item.icon || '🏆'}</span>
          {stage >= 2 && !reduce && (
            <div style={{
              position: 'absolute', top: '-30%', bottom: '-30%', left: 0, width: '35%',
              background: 'linear-gradient(105deg, transparent, rgba(var(--ink-rgb),0.55), transparent)',
              pointerEvents: 'none',
            }} className="au-shine" />
          )}
        </div>
      </div>

      {stage >= 3 && (
        <div style={{ textAlign: 'center' }} className={reduce ? undefined : 'au-text'}>
          <div style={{ color, fontSize: 11, fontWeight: 800, letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: 8 }}>{item.eyebrow || 'Achievement Unlocked'}</div>
          <div style={{ fontFamily: 'var(--font-serif)', color: '#fff', fontSize: 28, fontWeight: 600, marginBottom: 8 }}>{item.label}</div>
          {item.desc && <div style={{ color: 'rgba(var(--text-rgb),0.55)', fontSize: 14, maxWidth: 280, margin: '0 auto 22px' }}>{item.desc}</div>}
          <button onClick={(e) => { e.stopPropagation(); dismiss(); navigate(item.route || '/profile') }} style={{
            padding: '12px 22px', background: color, border: 'none', borderRadius: 'var(--radius)',
            color: 'var(--amber-ink)', fontWeight: 800, fontSize: 13.5, cursor: 'pointer', fontFamily: 'var(--font)',
          }}>{item.cta || 'See your run →'}</button>
          <div style={{ color: 'rgba(var(--ink-rgb),0.35)', fontSize: 10.5, marginTop: 16 }}>tap anywhere to continue</div>
        </div>
      )}

      <style>{`
        @keyframes auFadeIn { from{opacity:0} to{opacity:1} }
        @keyframes auMedallionDrop { 0%{opacity:0;transform:translateY(-40px) scale(0.7)} 60%{opacity:1;transform:translateY(6px) scale(1.05)} 80%{transform:translateY(-3px) scale(0.98)} 100%{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes auImpactShake { 0%{transform:scale(1)} 20%{transform:scale(0.94)} 45%{transform:scale(1.03)} 70%{transform:scale(0.99)} 100%{transform:scale(1)} }
        @keyframes auRingOut { 0%{opacity:0.8;transform:scale(0.3)} 100%{opacity:0;transform:scale(2.6)} }
        @keyframes auGlowPulse { 0%,100%{opacity:0.5} 50%{opacity:0.95} }
        @keyframes auTextUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        .au-fade-in { animation: auFadeIn 0.4s ease; }
        .au-ring { animation: auRingOut 0.9s ease-out forwards; }
        .au-ring-2 { animation-delay: 0.15s; }
        .au-glow { animation: auGlowPulse 2.2s ease-in-out infinite; }
        .au-drop { animation: auMedallionDrop 0.55s cubic-bezier(0.34,1.2,0.64,1) both; }
        .au-shake { animation: auImpactShake 0.5s ease; }
        .au-shine { animation: auShineSweep 0.9s ease-out forwards; }
        .au-text { animation: auTextUp 0.5s ease both; }
        @keyframes auShineSweep { 0%{transform:translateX(-160%) rotate(20deg)} 100%{transform:translateX(220%) rotate(20deg)} }
      `}</style>
    </div>
  ), document.body)
}
